/**
 * Meta Pixel, TikTok Pixel and Google Analytics 4: for the public pages only.
 *
 * Many PrepAI students are under 18, and the privacy policy promises no
 * behavioural advertising to minors. So the tags run only where we cannot yet
 * know someone's age — the landing page and the signup steps up to the age
 * check — and never inside the signed-in app. Events carry no email, name or
 * phone. A visitor can switch the tags off from the cookie notice.
 *
 * The IDs are v1's accounts, carried over: they are public by nature (any
 * page that loads a pixel shows its ID), so they live here as defaults. An env
 * var overrides one (`VITE_META_PIXEL_ID`, `VITE_TIKTOK_PIXEL_ID`,
 * `VITE_GA4_ID`), and setting it to `off` disables it. Tags load only when the
 * page is served from a production host, so local, preview, staging and test
 * runs send nothing whatever the IDs.
 */

/** Pages the tags may run on. Anything else — the app, parent links — never loads them. */
const PUBLIC_PATHS = ["/", "/signup", "/signup/details", "/verify", "/privacy", "/terms"];
const PRODUCTION_HOSTS = ["prepai.education", "www.prepai.education"];
const OPT_OUT_KEY = "prepai.ads.optout";
const NOTICE_KEY = "prepai.ads.notice";

/** v1's ad and analytics accounts (exam-prep index.html). */
export const DEFAULT_IDS = {
  meta: "274501613001085",
  tiktok: "D7JONJ3C77U3HEGUSC4G",
  ga4: "G-QNG4F3PL57",
} as const;

type Fn = ((...args: unknown[]) => void) & Record<string, unknown>;
type TagWindow = Window & {
  fbq?: Fn;
  _fbq?: Fn;
  ttq?: Fn & { load?: (id: string) => void; page?: () => void; track?: (...a: unknown[]) => void };
  TiktokAnalyticsObject?: string;
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

export interface TagConfig {
  meta?: string | undefined;
  tiktok?: string | undefined;
  ga4?: string | undefined;
  hosts?: string[];
}

/** An env override, the default when unset, or nothing when set to `off`. */
function pick(value: string | undefined, fallback: string): string | undefined {
  const v = value?.trim();
  if (!v) return fallback;
  return v.toLowerCase() === "off" ? undefined : v;
}

export const env = (vars: Partial<ImportMetaEnv> = import.meta.env): TagConfig => ({
  meta: pick(vars.VITE_META_PIXEL_ID, DEFAULT_IDS.meta),
  tiktok: pick(vars.VITE_TIKTOK_PIXEL_ID, DEFAULT_IDS.tiktok),
  ga4: pick(vars.VITE_GA4_ID, DEFAULT_IDS.ga4),
});

let loaded: { meta: boolean; tiktok: boolean; ga4: boolean } | null = null;

function store(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.includes(pathname.replace(/\/+$/, "") || "/");
}

export function optedOut(): boolean {
  try {
    return store()?.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

export function setOptOut(out: boolean): void {
  try {
    if (out) store()?.setItem(OPT_OUT_KEY, "1");
    else store()?.removeItem(OPT_OUT_KEY);
    store()?.setItem(NOTICE_KEY, "1");
  } catch {
    /* storage disabled: the choice lasts for this page */
  }
}

export function noticeSeen(): boolean {
  try {
    return store()?.getItem(NOTICE_KEY) === "1";
  } catch {
    return true;
  }
}

/** Whether tags may run here at all, before looking at the path. */
export function tagsAllowed(config: TagConfig = env(), hostname = window.location.hostname) {
  const hosts = config.hosts ?? PRODUCTION_HOSTS;
  return (
    Boolean(config.meta || config.tiktok || config.ga4) && hosts.includes(hostname) && !optedOut()
  );
}

function script(src: string): void {
  const tag = document.createElement("script");
  tag.async = true;
  tag.src = src;
  document.head.appendChild(tag);
}

/** The vendors' standard bootstraps, minus the parts that send data before we say so. */
function loadMeta(w: TagWindow, id: string): void {
  if (w.fbq) return;
  const fbq = function (...args: unknown[]) {
    const self = fbq as unknown as { callMethod?: (...a: unknown[]) => void; queue: unknown[] };
    if (self.callMethod) self.callMethod(...args);
    else self.queue.push(args);
  } as unknown as Fn;
  Object.assign(fbq, { push: fbq, loaded: true, version: "2.0", queue: [] });
  w.fbq = fbq;
  w._fbq = fbq;
  script("https://connect.facebook.net/en_US/fbevents.js");
  // Off before init: the pixel otherwise records a PageView on every
  // client-side route change by itself, which would follow a student into the
  // signed-in app. Page views are sent by `trackPageView`, public pages only.
  fbq("set", "autoConfig", false, id);
  w.fbq.disablePushState = true;
  fbq("init", id);
}

function loadTikTok(w: TagWindow, id: string): void {
  if (w.ttq) return;
  w.TiktokAnalyticsObject = "ttq";
  const queue: unknown[] = [];
  const ttq = queue as unknown as NonNullable<TagWindow["ttq"]>;
  for (const method of [
    "page",
    "track",
    "identify",
    "instances",
    "debug",
    "on",
    "off",
    "once",
    "ready",
  ]) {
    (ttq as Record<string, unknown>)[method] = (...args: unknown[]) =>
      queue.push([method, ...args]);
  }
  // The vendor's `load`: records the pixel ID and fetches its script, which
  // then replays the queue. Without it the pixel never starts.
  ttq.load = (sdkid: string) => {
    const store = ttq as unknown as Record<string, Record<string, unknown>>;
    store._i = { ...(store._i ?? {}), [sdkid]: [] };
    store._t = { ...(store._t ?? {}), [sdkid]: Date.now() };
    store._o = { ...(store._o ?? {}), [sdkid]: {} };
    script(
      `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${encodeURIComponent(sdkid)}&lib=ttq`,
    );
  };
  w.ttq = ttq;
  ttq.load(id);
}

function loadGa4(w: TagWindow, id: string): void {
  if (w.gtag) return;
  w.dataLayer = w.dataLayer ?? [];
  w.gtag = function gtag() {
    // gtag reads `arguments` itself, so it must not be spread into an array.
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments);
  };
  script(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`);
  w.gtag("js", new Date());
  // Page views are sent by `trackPageView`, so client-side routing is counted
  // once per page. Ads personalisation is off: these may be minors' visits.
  w.gtag("config", id, {
    send_page_view: false,
    // GA4's enhanced measurement counts history changes as page views by
    // itself; that would follow a student into the app. This keeps it to the
    // events `trackPageView` sends from public pages.
    page_changes: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

/**
 * Load the tags, once, if this page and visitor allow it. Safe to call on every
 * route change; returns whether anything is loaded.
 */
export function loadTags(
  pathname = window.location.pathname,
  config: TagConfig = env(),
  hostname = window.location.hostname,
): boolean {
  if (loaded) return true;
  if (!isPublicPath(pathname) || !tagsAllowed(config, hostname)) return false;
  const w = window as TagWindow;
  loaded = { meta: false, tiktok: false, ga4: false };
  if (config.meta) {
    loadMeta(w, config.meta);
    loaded.meta = true;
  }
  if (config.tiktok) {
    loadTikTok(w, config.tiktok);
    loaded.tiktok = true;
  }
  if (config.ga4) {
    loadGa4(w, config.ga4);
    loaded.ga4 = true;
  }
  return true;
}

/** A page view, for a public page only. */
export function trackPageView(pathname = window.location.pathname): void {
  if (!loaded || !isPublicPath(pathname) || optedOut()) return;
  const w = window as TagWindow;
  if (loaded.meta) w.fbq?.("track", "PageView");
  if (loaded.tiktok) w.ttq?.page?.();
  if (loaded.ga4) w.gtag?.("event", "page_view", { page_path: pathname });
}

/**
 * The one conversion the ad platforms optimise for: an account was created.
 * Sent with no identifying data — no email, name or phone.
 */
export function trackSignup(): void {
  if (!loaded || optedOut()) return;
  const w = window as TagWindow;
  if (loaded.meta) w.fbq?.("track", "CompleteRegistration");
  if (loaded.tiktok) w.ttq?.track?.("CompleteRegistration");
  if (loaded.ga4) w.gtag?.("event", "sign_up", { method: "email" });
}

/** For tests: forget what was loaded. */
export function resetTagsForTests(): void {
  loaded = null;
  const w = window as TagWindow;
  delete w.fbq;
  delete w._fbq;
  delete w.ttq;
  delete w.gtag;
  delete w.dataLayer;
}
